import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import PermissionButton from 'components/buttons/PermissionButton';
import { ReceiptLongOutlined as ReceiptLongOutlinedIcon } from 'components/icons';
import { LineTab, LineTabs, LineTabsList } from 'components/LineTabs';
import { Tooltip } from 'components/ui/tooltip';
import { useLocalStorage } from '@uidotdev/usehooks';
import Big from 'big.js';
import Loader from 'components/Loader';
import { DateDefaultNowrap } from 'components/misc/DateDefaultNowrap.tsx';
import EntityLinkList from 'components/misc/EntityLinkList.tsx';
import LineClamp from 'components/misc/LineClamp.tsx';
import ListClamp from 'components/misc/ListClamp.tsx';
import SendInvoicesButton from 'components/modals/invoices/SendInvoices.tsx';
import PartyName from 'components/party/PartyName';
import { RecordLink } from 'components/record-chip/RecordLink';
import type { CellContext } from '@tanstack/react-table';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { getColumnId } from 'components/tables/list-table/columnLayout/getColumnId';
import { ListViewsPageContext, withSavedView } from 'components/tables/list-table/filter-bar/builtInViews';
import { useRecordTabBuiltIn } from 'components/tables/list-table/filter-bar/ListViewsScope';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { tableLoadingProps } from 'components/tables/list-table/tableLoadingProps';
import TooltipHeader from 'components/tables/TooltipHeader';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { cn } from 'utils/cn';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useStartInvoiceCRExportMutation, useStartInvoiceExportMutation } from 'store/api/exports';
import {
    useGetInvoicesTotalsQuery,
    useGetInvoiceTemplatesQuery,
    useLazyGetInvoicesCountQuery,
    useLazyGetInvoicesWithFiltersQuery,
} from 'store/api/invoices';
import { RootState } from 'store/reducers';
import { clearBulkSendInvoices, filterVisibleSelections } from 'store/reducers/invoices.ts';
import { EXPORT_CONFIG } from 'types/exports';
import { FeatureFlag } from 'types/features.ts';
import {
    InvoiceDataListItem,
    InvoiceEmailTemplateType,
    InvoiceStatusType,
    InvoicesTotalsDto,
    InvoiceType,
} from 'types/invoices';
import { PaginatedFieldPassiveFilter } from 'utils/filterUtil.ts';
import { getClassifierText, t } from 'utils/getTranslations';
import { useLastNameFirst } from 'utils/nameOrder';
import { officeInactiveSuffix } from 'utils/officeOptionRenderer';
import { useHasPermissions } from 'utils/permissionUtil';
import { useAsyncExport } from 'utils/useAsyncExport.ts';
import { useClassifiers } from 'utils/useClassifiers';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';
import {
    BulkSendCheckbox,
    DebtReminderCheckbox,
    FeesWithDiscount,
    FeesWithoutDiscount,
    InvoiceAmountDisplay,
    InvoiceBrutoTotal,
    InvoiceClient,
    InvoiceClientPayer,
    InvoiceCreditInvoices,
    InvoiceDate,
    InvoiceDeadline,
    InvoiceExpensesTotal,
    InvoiceFeesTotal,
    InvoiceHours,
    InvoiceIconGroup,
    InvoiceMatter,
    InvoiceNumber,
    InvoiceNumberClientPayer,
    InvoicePaymentShare,
    InvoiceOutstandingBalance,
    InvoicePrepaymentAmount,
    InvoiceRelationshipPartner,
    InvoiceStatus,
    InvoiceStatusChange,
    InvoiceVatTotal,
    NonVatExpensesWithoutDiscount,
    SentPdfCell,
    VatAppliedExpensesWithoutDiscount,
} from './InvoiceTableCells.tsx';
import { AddDebtNoteButton } from './layout/views/components/AddDebtNoteButton.tsx';
import { BulkSelectButton } from './layout/views/components/BulkSelectButton.tsx';
import { DebtReminderButton } from './layout/views/components/DebtReminderButton.tsx';
import {
    type FilterGroup,
    type FilterId,
    INVOICE_LIST_CLASSIFIERS,
    KSEF_EXTERNAL_ACTION_TRANSLATION_KEYS,
    KSEF_EXTERNAL_STATE_TRANSLATION_KEYS,
    KSEF_STATUS_TRANSLATION_KEYS,
    useInvoiceFilterConfig,
} from './useInvoiceFilterConfig';
import { getBudgetInfo, getDaysOverdue, invoicePath, isDebtReminderEligible } from './util/invoiceUtil';

/** Cap on the invoices the totals aggregation covers, mirroring `InvoiceBizService.TOTALS_RECORD_LIMIT`:
 *  above it the backend answers `limitExceeded` instead of summing. */
const TOTALS_RECORD_LIMIT = 60_000;

// A nowrap date (90px for a dash format in the classic design) + the cell's 32px inset.
const DATE_COLUMN_MIN_PX = 124;
// The same date led, in the classic design, by the type chip column: a 27px chip + its 16px gap (165px of cell).
const DATE_WITH_TYPE_COLUMN_MIN_PX = DATE_COLUMN_MIN_PX + 43;

const renderMoneyTotal =
    (field: keyof InvoicesTotalsDto) =>
    (totals: InvoicesTotalsDto | undefined): React.ReactNode =>
        totals ? <MoneyDisplay amount={String(totals[field] ?? '0')} currency={totals.baseCurrencyTypeCode} /> : null;

export enum InvoiceTableTab {
    MAIN = 0,
    LAWYERS = 1,
    REVENUE_TEAM = 2,
    CREDIT_MANAGEMENT = 3,
    ACCOUNTING = 4,
    KSEF = 5,
}

export const TAB_URL_PARAM_MAP: Record<string, InvoiceTableTab> = {
    main: InvoiceTableTab.MAIN,
    law: InvoiceTableTab.LAWYERS,
    rev: InvoiceTableTab.REVENUE_TEAM,
    cr: InvoiceTableTab.CREDIT_MANAGEMENT,
    acct: InvoiceTableTab.ACCOUNTING,
    ksef: InvoiceTableTab.KSEF,
};

export const TAB_INDEX_TO_URL_KEY: Record<InvoiceTableTab, string> = Object.entries(TAB_URL_PARAM_MAP).reduce(
    (acc, [key, value]) => {
        acc[value] = key;
        return acc;
    },
    {} as Record<InvoiceTableTab, string>
);

export const DEFAULT_INVOICE_TAB_URL_KEY = 'main';

const TAB_URL_KEYS = Object.keys(TAB_URL_PARAM_MAP);

// Tabs whose columns are pooled into every tab's column picker. KSeF is deliberately excluded
// as a source so its e-invoicing columns never surface on the other tabs; the KSeF tab still
// keeps its own columns and, like the rest, gains the pooled columns as opt-in extras.
const SHAREABLE_SOURCE_TABS: InvoiceTableTab[] = [
    InvoiceTableTab.MAIN,
    InvoiceTableTab.LAWYERS,
    InvoiceTableTab.REVENUE_TEAM,
    InvoiceTableTab.CREDIT_MANAGEMENT,
    InvoiceTableTab.ACCOUNTING,
];

// Interactive control columns that only make sense inside their own tab's workflow (the bulk
// send / debt-reminder row checkbox share the id `select`), so they are never pooled elsewhere.
const NON_SHAREABLE_COLUMN_IDS = new Set<string>(['select']);

/**
 * Column set for the active tab: the tab's own columns first, then every other poolable column
 * appended as a hidden-by-default extra so any column can be revealed in any tab. Extras are
 * de-duped by column id across the source tabs and stripped of the source tab's sticky / lock /
 * fixed-width flags so they behave as ordinary optional columns here (an extra that kept
 * `lockVisible`/`fixedPx` would be treated as locked and thus never actually hide).
 */
const buildTabColumns = (views: InvoiceColumn[][], activeTab: InvoiceTableTab): InvoiceColumn[] => {
    // Fall back to the main view if `activeTab` is ever out of range (e.g. a stale persisted
    // tab index), so an invalid value can't make `base` undefined and crash the iteration.
    const base = views[activeTab] ?? views[InvoiceTableTab.MAIN] ?? [];
    const seen = new Set<string>();
    for (const column of base) {
        const id = getColumnId(column);
        if (id) seen.add(id);
    }
    const extras: InvoiceColumn[] = [];
    for (const sourceTab of SHAREABLE_SOURCE_TABS) {
        if (sourceTab === activeTab) continue;
        for (const column of views[sourceTab] ?? []) {
            const id = getColumnId(column);
            if (!id || seen.has(id) || NON_SHAREABLE_COLUMN_IDS.has(id)) continue;
            seen.add(id);
            extras.push({
                ...column,
                meta: { ...column.meta, defaultHidden: true, sticky: false, lockVisible: false, fixedPx: undefined },
            });
        }
    }
    return extras.length === 0 ? base : [...base, ...extras];
};

/** An invoices column. The totals type is pinned so a `meta.renderTotal` callback can take the
 *  concrete `InvoicesTotalsDto` the tab's totals query returns. */
type InvoiceColumn = DataTableColumnDef<InvoiceDataListItem, InvoicesTotalsDto | undefined>;

/**
 * The one case column every tab uses: case number with the case name below it. Accounting has no
 * case column of its own and picks this up from the shared pool as an opt-in hidden extra. Built on
 * call rather than held as a module constant so the header is translated in the active locale.
 */
const buildCaseColumn = (): InvoiceColumn => ({
    header: t('matters.number-and-name'),
    id: 'matters',
    enableSorting: false,
    meta: {
        widthWeight: 1.5,
        minPx: 160,
    },
    cell: ({ row }) => <InvoiceMatter invoice={row.original} fullName />,
});

type Props = {
    passiveFilter?: string;
    disableUrlState?: boolean;
    matterId?: string;
    contactId?: string;
    disableExport?: boolean;
    disabledGroups?: FilterGroup[];
    disabledFilterIds?: FilterId[];
    paginatedFieldPassiveFilters?: PaginatedFieldPassiveFilter<FilterId>[];
    showOnDemandTotals?: boolean;
    /** Initial value for the "Show totals" toggle. Defaults to false. */
    defaultShowTotals?: boolean;
    /**
     * When set, the active tab is driven by this URL key (e.g. `'main'`, `'law'`) and tab
     * changes navigate by replacing the last segment of the current pathname. Used by the
     * standalone invoices page and the contact/matter embeds. Leave undefined for embeds
     * that should fall back to the legacy ?invoiceTab= + localStorage flow, which the new
     * design replaces with the view the URL names.
     */
    pathBasedTabId?: string;
    extraHeaderComponent?: React.ReactNode;
};

const InvoicesTable = ({
    passiveFilter = '',
    disableUrlState = false,
    matterId,
    contactId,
    disableExport = false,
    disabledGroups,
    disabledFilterIds,
    paginatedFieldPassiveFilters,
    showOnDemandTotals = false,
    defaultShowTotals = false,
    pathBasedTabId,
    extraHeaderComponent,
}: Props) => {
    const navigateWithReturn = useNavigateWithReturnState();
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: INVOICE_LIST_CLASSIFIERS,
    });
    const [fetchData, { data, currentData, isFetching, isLoading }] = useLazyGetInvoicesWithFiltersQuery();
    const tableLoading = tableLoadingProps({ isLoading, isFetching, currentData });
    const totalsEnabled = showOnDemandTotals || defaultShowTotals;
    const dispatch = useDispatch();
    const { data: invoiceTemplateList, isLoading: isLoadingInvoiceTemplates } = useGetInvoiceTemplatesQuery();
    const hasPermissionToExportInvoices = useHasPermissions(['EXPORT.INVOICES']);
    const isSortingEnabled = useIsFeatureEnabled(FeatureFlag.SORTING_INVOICES);
    const isNewDesign = useIsNewDesign();
    const lastNameFirst = useLastNameFirst();
    const isBulkSendEnabled = useIsFeatureEnabled(FeatureFlag.BULK_SEND_INVOICES);
    const isCRExportEnabled = useIsFeatureEnabled(FeatureFlag.SHORT_INVOICE_EXCEL_CR);
    const isKsefTabEnabled = useIsFeatureEnabled(FeatureFlag.KSEF_INVOICES_LIST);
    const isSharedPaymentEnabled = useIsFeatureEnabled(FeatureFlag.INVOICE_SHARED_PAYMENT_FUNCTIONALITY);
    const selectedBulkSendInvoiceIds = useSelector((state: RootState) => state.invoices.selectedBulkSendInvoiceIds);

    // **AUTOMATIC SELECTION FILTERING**: Filter out selections for invoices that are no longer visible
    // This ensures hidden items are not selected after filter changes
    useEffect(() => {
        if (data?.content) {
            const visibleIds = data.content.map((invoice) => invoice.id);
            dispatch(filterVisibleSelections(visibleIds));
        }
    }, [data, dispatch]);

    const { isBusy: isExportDataFetching, triggerManualDownload } = useAsyncExport(useStartInvoiceExportMutation, {
        entityType: 'INVOICE',
        entityName: t('invoices.export.entity-name'),
        fileName: EXPORT_CONFIG.INVOICE.fileName,
    });

    const { isBusy: isCRExportDataFetching, triggerManualDownload: triggerCRDownload } = useAsyncExport(
        useStartInvoiceCRExportMutation,
        {
            entityType: 'INVOICE_CR',
            entityName: t('invoices.export.entity-name'),
            fileName: EXPORT_CONFIG.INVOICE_CR.fileName,
        }
    );

    const globalView = useMemo<InvoiceColumn[]>(
        () => [
            {
                header: t('invoices.invoice-no-client-payer'),
                id: 'invoiceNumber',
                enableGrouping: false,
                accessorKey: 'invoiceNumber',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sticky: true,
                    lockVisible: true,
                    widthWeight: 2.5,
                    minPx: 240,
                },
                cell: ({ row }) => (
                    <InvoiceNumberClientPayer
                        invoice={row.original}
                        classifiers={classifiers}
                        contactId={contactId}
                        matterId={matterId}
                    />
                ),
            },
            buildCaseColumn(),
            {
                header: t('date'),
                id: 'invoiceDate',
                accessorKey: 'invoiceDate',
                enableSorting: !!isSortingEnabled,
                meta: {
                    widthWeight: 0.8,
                    minPx: DATE_COLUMN_MIN_PX,
                },
                cell: ({ getValue }) => {
                    const value = getValue();
                    return value && <DateDefaultNowrap dateValue={value} />;
                },
            },
            {
                header: t('invoices.due-date'),
                id: 'dueDate',
                accessorKey: 'dueDate',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'dueDate',
                    widthWeight: 0.8,
                    minPx: DATE_COLUMN_MIN_PX,
                    nullHandling: 'nullsLast',
                },
                cell: ({ row }) => {
                    const invoice = row.original;
                    if (!invoice.dueDate) return null;
                    const today = new Date();
                    const dueDate = new Date(invoice.dueDate);
                    const isPastDate = dueDate < today;
                    const isStatusSent = invoice.invoiceStatusTypeCode === InvoiceStatusType.SENT;
                    const isOutstandingGreaterThanZero = new Big(invoice.outstandingBalanceInPaymentCurrency)
                        .abs()
                        .gt(0);
                    const isDebitOrPrepayment =
                        invoice.invoiceTypeCode === InvoiceType.DEBIT ||
                        invoice.invoiceTypeCode === InvoiceType.PREPAYMENT;

                    const shouldColorRed =
                        isPastDate && isStatusSent && isOutstandingGreaterThanZero && isDebitOrPrepayment;

                    return (
                        <DateDefaultNowrap
                            dateValue={invoice.dueDate}
                            className={shouldColorRed ? 'text-error' : undefined}
                        />
                    );
                },
            },
            {
                header: t('invoices.status'),
                id: 'invoiceStatus',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 130,
                },
                cell: ({ row }) => <InvoiceStatus invoice={row.original} classifiers={classifiers} />,
            },
            {
                header: t('invoices.budget'),
                id: 'budget',
                enableGrouping: false,
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    // Fits the nowrap "Fixed fee 10 000.00 EUR" label+amount pair.
                    minPx: 150,
                },
                cell: ({ row }) => {
                    const { mainMatter } = row.original;
                    const budgetInfo = getBudgetInfo(mainMatter);
                    return budgetInfo ? <div>{budgetInfo}</div> : null;
                },
            },
            {
                header: t('invoices.fees-total'),
                id: 'invoicesFeesTotal',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 140,
                    renderTotal: renderMoneyTotal('totalFeesBaseCurrency'),
                },
                cell: ({ row }) => <InvoiceFeesTotal invoice={row.original} />,
            },
            {
                header: t('invoices.expenses-total'),
                id: 'invoicesExpensesTotal',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 140,
                    renderTotal: renderMoneyTotal('totalExpensesBaseCurrency'),
                },
                cell: ({ row }) => <InvoiceExpensesTotal invoice={row.original} />,
            },
            {
                header: t('stats.total-amount'),
                id: 'invoicesTotalAmount',
                accessorKey: 'invoiceTotals',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'totals.baseCurrency.invoice.totalWithoutVat',
                    widthWeight: 1,
                    minPx: 140,
                    renderTotal: renderMoneyTotal('totalAmountBaseCurrency'),
                },
                cell: ({ row }) => <InvoiceAmountDisplay invoice={row.original} fieldName="totalWithoutVat" />,
            },
            ...(isSharedPaymentEnabled
                ? [
                      {
                          header: t('invoices.payers.list-column'),
                          id: 'paymentShareRate',
                          accessorKey: 'paymentShareRate',
                          enableSorting: !!isSortingEnabled,
                          meta: {
                              sortAccessor: 'paymentShareRate',
                              nullHandling: 'nullsLast',
                              widthWeight: 0.7,
                              minPx: 128,
                              textAlign: 'right',
                          },
                          cell: ({ row }) => <InvoicePaymentShare invoice={row.original} />,
                      } satisfies InvoiceColumn,
                  ]
                : []),
            {
                header: () => (
                    <TooltipHeader
                        label="invoices.final-payable-amount"
                        tooltip="invoices.tooltips.final-payable-amount"
                    />
                ),
                id: 'finalPayableAmount',
                accessorKey: 'finalPayableAmountInPaymentCurrency',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'paymentsJson.baseCurrency.amountDue',
                    widthWeight: 1,
                    minPx: 140,
                    pickerLabel: t('invoices.final-payable-amount'),
                    renderTotal: renderMoneyTotal('totalFinalPayableBaseCurrency'),
                },
                cell: ({ row }) => <InvoiceAmountDisplay invoice={row.original} fieldName="amountDue" />,
            },
            {
                header: t('invoices.outstanding-balance'),
                id: 'invoicesOutstandingBalance',
                accessorKey: 'outstandingBalanceInPaymentCurrency',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'paymentsJson.baseCurrency.unpaidAmount',
                    widthWeight: 1,
                    minPx: 140,
                    renderTotal: renderMoneyTotal('totalOutstandingBalanceBaseCurrency'),
                },
                cell: ({ row }) => <InvoiceOutstandingBalance invoice={row.original} />,
            },
        ],
        [isSortingEnabled, isSharedPaymentEnabled, classifiers, contactId, matterId]
    );

    const lawyersView = useMemo<InvoiceColumn[]>(
        () => [
            {
                header: t('invoices.status-change'),
                id: 'statusChange',
                accessorKey: 'statusChangeDate',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'sortParamsJson.lastStatusChangeDate',
                    nullHandling: 'nullsLast',
                    sticky: true,
                    lockVisible: true,
                    widthWeight: 1,
                    minPx: DATE_WITH_TYPE_COLUMN_MIN_PX,
                },
                cell: ({ row }) => {
                    const icons = (
                        <InvoiceIconGroup
                            typeCode={row.original.invoiceTypeCode}
                            typeLabel={getClassifierText(classifiers['INVOICE_TYPE'], row.original.invoiceTypeCode)}
                            isJointInvoice={row.original.matters.length > 1}
                            isSplit={row.original.isSplit ?? false}
                            row={isNewDesign}
                        />
                    );
                    // The new design puts the type beside the status, on the cell's bottom line.
                    return isNewDesign ? (
                        <InvoiceStatusChange
                            invoice={row.original}
                            classifiers={classifiers}
                            contactId={contactId}
                            matterId={matterId}
                            chips={icons}
                        />
                    ) : (
                        <div className="flex flex-row">
                            {icons}
                            <InvoiceStatusChange
                                invoice={row.original}
                                classifiers={classifiers}
                                contactId={contactId}
                                matterId={matterId}
                            />
                        </div>
                    );
                },
            },
            {
                header: t('invoices.invoice-no-client-payer'),
                id: 'invoiceNumber',
                enableGrouping: false,
                accessorKey: 'invoiceNumber',
                enableSorting: !!isSortingEnabled,
                meta: {
                    widthWeight: 2.5,
                    minPx: 240,
                },
                cell: ({ row }) => (
                    // The status-change column of this view already renders the icon group.
                    <InvoiceNumberClientPayer
                        invoice={row.original}
                        classifiers={classifiers}
                        contactId={contactId}
                        matterId={matterId}
                        hideIcons
                    />
                ),
            },
            buildCaseColumn(),
            {
                header: t('invoices.case-responsible-persons'),
                id: 'relationshipPartner',
                enableSorting: false,
                meta: {
                    widthWeight: 1.2,
                    minPx: 140,
                },
                cell: ({ row }) => <InvoiceRelationshipPartner invoice={row.original} />,
            },
            // The two halves of the column above as separately pickable, sortable columns, rendering the
            // bare name where the combined one prefixes "SP:" / "RL:". Declared here only; the shared pool
            // hands them to every other tab as hidden-by-default extras.
            {
                header: t('matters.supervising-partner'),
                id: 'supervisingPartner',
                // A column is sortable only when it has an accessor; the value itself is unused (the cell
                // overrides rendering). The sort key is the main case's partner — the same main-case-only
                // convention every other case-derived sort key on this list uses — while a joint invoice's
                // cell lists all of them. sortAccessor follows the displayed name order (LAST_NAME_FIRST).
                accessorKey: 'supervisingPartners',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: lastNameFirst
                        ? 'matter.supervisedByEmployee.lastName'
                        : 'matter.supervisedByEmployee.firstName',
                    defaultHidden: true,
                    widthWeight: 1.2,
                    minPx: 140,
                },
                cell: ({ row }) => <EntityLinkList domain="employee" items={row.original.supervisingPartners} />,
            },
            {
                header: t('matters.responsible-lawyer'),
                id: 'responsibleLawyer',
                accessorKey: 'responsibleLawyers',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: lastNameFirst
                        ? 'matter.responsibleEmployee.lastName'
                        : 'matter.responsibleEmployee.firstName',
                    defaultHidden: true,
                    widthWeight: 1.2,
                    minPx: 140,
                },
                cell: ({ row }) => <EntityLinkList domain="employee" items={row.original.responsibleLawyers} />,
            },
            {
                header: t('invoices.cap-fixed-fee'),
                id: 'budget',
                enableGrouping: false,
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    // Fits the nowrap "Fixed fee 10 000.00 EUR" label+amount pair.
                    minPx: 150,
                },
                cell: ({ row }) => {
                    const { mainMatter } = row.original;
                    const budgetInfo = getBudgetInfo(mainMatter);
                    return budgetInfo ? <div>{budgetInfo}</div> : null;
                },
            },
            {
                header: t('invoices.currency'),
                accessorKey: 'paymentCurrencyTypeCode',
                enableSorting: false,
                meta: {
                    widthWeight: 0.5,
                    minPx: 70,
                },
                cell: ({ getValue }) => getValue() ?? '',
            },
            {
                header: t('invoices.net-total'),
                id: 'totalAmount',
                accessorKey: 'invoiceTotals',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'totals.baseCurrency.invoice.totalWithoutVat',
                    widthWeight: 1,
                    minPx: 140,
                    renderTotal: renderMoneyTotal('totalAmountBaseCurrency'),
                },
                cell: ({ row }) => <InvoiceAmountDisplay invoice={row.original} fieldName="totalWithoutVat" />,
            },
            {
                header: t('invoices.fees'),
                id: 'fees',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 140,
                    renderTotal: renderMoneyTotal('totalFeesWithoutDiscountBaseCurrency'),
                },
                cell: ({ row }) => <FeesWithoutDiscount invoice={row.original} />,
            },
            {
                header: t('invoices.vat-applied-expenses'),
                id: 'expenses',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 140,
                    renderTotal: renderMoneyTotal('totalVatExpensesWithoutDiscountBaseCurrency'),
                },
                cell: ({ row }) => <VatAppliedExpensesWithoutDiscount invoice={row.original} />,
            },
            {
                header: t('invoices.non-vat-expenses'),
                id: 'nonVatExpenses',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 140,
                    renderTotal: renderMoneyTotal('totalNonVatExpensesWithoutDiscountBaseCurrency'),
                },
                cell: ({ row }) => <NonVatExpensesWithoutDiscount invoice={row.original} />,
            },
            {
                header: t('invoices.due-date'),
                id: 'dueDate',
                accessorKey: 'dueDate',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'dueDate',
                    nullHandling: 'nullsLast',
                    widthWeight: 0.8,
                    minPx: DATE_COLUMN_MIN_PX,
                },
                cell: ({ getValue }) => {
                    const value = getValue();
                    return value && <DateDefaultNowrap dateValue={value} />;
                },
            },
            {
                header: t('invoices.paid-on'),
                id: 'paidOnDate',
                accessorKey: 'paidOnDate',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'paidOnDate',
                    nullHandling: 'nullsLast',
                    widthWeight: 0.8,
                    minPx: DATE_COLUMN_MIN_PX,
                },
                cell: ({ getValue }) => {
                    const value = getValue();
                    return value && <DateDefaultNowrap dateValue={value} />;
                },
            },
        ],
        [isSortingEnabled, lastNameFirst, classifiers, contactId, matterId, isNewDesign]
    );

    const revenueTeamView = useMemo<InvoiceColumn[]>(
        () => [
            ...(isBulkSendEnabled
                ? [
                      {
                          id: 'select',
                          meta: {
                              // Checkbox + cell padding fit in 64px.
                              fixedPx: 64,
                              sticky: true,
                          },
                          cell: ({ row }: CellContext<InvoiceDataListItem, unknown>) => {
                              if (InvoiceStatusType.FINALIZED !== row.original.invoiceStatusTypeCode) {
                                  return null;
                              }
                              return <BulkSendCheckbox invoiceId={row.original.id} />;
                          },
                          enableGrouping: false,
                          enableSorting: false,
                      },
                  ]
                : []),
            {
                header: t('date'),
                id: 'invoiceDate',
                accessorKey: 'invoiceDate',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sticky: true,
                    lockVisible: true,
                    widthWeight: 0.9,
                    minPx: DATE_WITH_TYPE_COLUMN_MIN_PX,
                },
                cell: ({ row }) => <InvoiceDate invoice={row.original} classifiers={classifiers} />,
            },
            {
                header: t('invoices.due-date'),
                id: 'dueDate',
                accessorKey: 'dueDate',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'dueDate',
                    widthWeight: 0.8,
                    minPx: DATE_COLUMN_MIN_PX,
                    nullHandling: 'nullsLast',
                },
                cell: ({ getValue }) => {
                    const value = getValue();
                    return value && <DateDefaultNowrap dateValue={value} />;
                },
            },
            {
                header: t('invoices.status-change'),
                id: 'statusChange',
                accessorKey: 'statusChangeDate',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'sortParamsJson.lastStatusChangeDate',
                    nullHandling: 'nullsLast',
                    widthWeight: 1.2,
                    // A nowrap date over a nowrap status label; no type chip in this cell.
                    minPx: 160,
                },
                cell: ({ row }) => (
                    <InvoiceStatusChange
                        invoice={row.original}
                        classifiers={classifiers}
                        contactId={contactId}
                        matterId={matterId}
                    />
                ),
            },
            {
                header: t('invoices.deadline'),
                id: 'deadline',
                enableSorting: false,
                meta: {
                    widthWeight: 0.8,
                    minPx: 120,
                },
                cell: ({ row }) => <InvoiceDeadline invoice={row.original} />,
            },
            {
                header: t('invoices.invoice-number'),
                id: 'invoiceNumber',
                accessorKey: 'invoiceNumber',
                enableSorting: !!isSortingEnabled,
                meta: {
                    widthWeight: 1,
                    minPx: 130,
                },
                cell: ({ row }) => <InvoiceNumber invoice={row.original} contactId={contactId} matterId={matterId} />,
            },
            {
                header: t('invoices.client-payer'),
                id: 'clientPayer',
                accessorKey: 'clientParty',
                enableSorting: !!isSortingEnabled,
                meta: {
                    widthWeight: 1.5,
                    minPx: 160,
                    sortAccessor: 'clientParty.name',
                },
                cell: ({ row }) => <InvoiceClientPayer invoice={row.original} />,
            },
            buildCaseColumn(),
            {
                header: t('invoices.currency'),
                accessorKey: 'paymentCurrencyTypeCode',
                enableSorting: false,
                meta: {
                    widthWeight: 0.5,
                    minPx: 70,
                },
                cell: ({ getValue }) => getValue() ?? '',
            },
            {
                header: t('invoices.hours'),
                id: 'invoiceHours',
                enableSorting: false,
                meta: {
                    widthWeight: 0.7,
                    // Fits nowrap negative totals like "-255.70 h".
                    minPx: 96,
                },
                cell: ({ row }) => <InvoiceHours invoice={row.original} />,
            },
            {
                header: t('invoices.gross-total'),
                id: 'grossTotal',
                accessorKey: 'invoiceTotals',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'totals.baseCurrency.invoice.totalWithVat',
                    widthWeight: 1,
                    minPx: 140,
                    renderTotal: renderMoneyTotal('totalGrossTotalBaseCurrency'),
                },
                cell: ({ row }) => <InvoiceBrutoTotal invoice={row.original} />,
            },
            {
                header: t('invoices.prepayment'),
                id: 'prepayment',
                accessorKey: 'invoiceTotals',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'paymentsJson.baseCurrency.prepaymentAmount',
                    widthWeight: 1,
                    minPx: 140,
                    renderTotal: renderMoneyTotal('totalPrepaymentBaseCurrency'),
                },
                cell: ({ row }) => <InvoicePrepaymentAmount invoice={row.original} />,
            },
            {
                header: () => (
                    <TooltipHeader
                        label="invoices.final-payable-amount"
                        tooltip="invoices.tooltips.final-payable-amount"
                    />
                ),
                id: 'finalPayableAmount',
                accessorKey: 'finalPayableAmountInPaymentCurrency',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'paymentsJson.baseCurrency.amountDue',
                    widthWeight: 1,
                    minPx: 140,
                    pickerLabel: t('invoices.final-payable-amount'),
                    renderTotal: renderMoneyTotal('totalFinalPayableBaseCurrency'),
                },
                cell: ({ row }) => <InvoiceAmountDisplay invoice={row.original} fieldName="amountDue" />,
            },
            {
                header: t('invoices.specific-remarks'),
                accessorKey: 'billingInstructions',
                enableSorting: !!isSortingEnabled,
                meta: {
                    widthWeight: 1,
                    minPx: 120,
                },
                cell: ({ getValue }) => <LineClamp>{getValue()}</LineClamp>,
            },
        ],
        [isBulkSendEnabled, isSortingEnabled, classifiers, contactId, matterId]
    );

    const creditManagementView = useMemo<InvoiceColumn[]>(
        () => [
            {
                id: 'select',
                meta: {
                    // Checkbox + cell padding fit in 64px.
                    fixedPx: 64,
                    sticky: true,
                },
                cell: ({ row }) => <DebtReminderCheckbox invoice={row.original} />,
                enableGrouping: false,
                enableSorting: false,
            },
            {
                id: 'invoiceDate',
                accessorKey: 'invoiceDate',
                header: t('date'),
                enableSorting: !!isSortingEnabled,
                meta: {
                    sticky: true,
                    lockVisible: true,
                    widthWeight: 1,
                    minPx: DATE_WITH_TYPE_COLUMN_MIN_PX,
                },
                cell: ({ row }) => <InvoiceDate invoice={row.original} classifiers={classifiers} showStatus />,
            },
            {
                header: t('invoices.due-date'),
                id: 'dueDate',
                accessorKey: 'dueDate',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'dueDate',
                    nullHandling: 'nullsLast',
                    widthWeight: 0.8,
                    minPx: DATE_COLUMN_MIN_PX,
                },
                cell: ({ getValue }) => {
                    const value = getValue();
                    return value && <DateDefaultNowrap dateValue={value} />;
                },
            },
            // Declared here only; the shared pool hands it to every other tab as a hidden-by-default extra.
            // The same value the backend's `daysOverdue` sort key orders by and its filter compares against,
            // and the same value both exports carry.
            {
                header: t('invoices.days-overdue'),
                id: 'daysOverdue',
                // The due date is what this derives from; a column is sortable only with an accessor, and
                // the explicit id keeps it distinct from the due-date column that shares the accessor.
                accessorKey: 'dueDate',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'daysOverdue',
                    nullHandling: 'nullsLast',
                    defaultHidden: true,
                    widthWeight: 0.6,
                    minPx: 110,
                },
                cell: ({ getValue }) => {
                    const days = getDaysOverdue(getValue<string | null>());
                    if (days === null) return null;
                    return <span className={cn(days > 0 && 'text-error')}>{days}d</span>;
                },
            },
            {
                header: t('invoices.debt-collection-notes'),
                accessorKey: 'debtNotes',
                enableSorting: false,
                meta: {
                    // The ListClamp content below declares minWidth: 300px — the column floor must
                    // cover it PLUS the cell's horizontal padding (16px per side) or the clamped
                    // text clips at smaller widths.
                    widthWeight: 2.2,
                    minPx: 340,
                },
                cell: ({ getValue, row }) => {
                    const items = getValue<string | null>()?.split('\n') || [];
                    return (
                        <ListClamp
                            items={items}
                            maxVisibleItems={1}
                            maxTooltipHeight="50dvh"
                            linkTo={`/invoice/${row.original.id}#debt-notes`}
                            linkTarget="_blank"
                            getKey={(_, index) => index}
                            renderItem={(sentence, index) => (
                                <p className={cn('text-body1', index !== items.length - 1 && 'mb-2')}>
                                    {sentence.trim()}
                                </p>
                            )}
                            className="w-fit min-w-[300px]"
                        />
                    );
                },
            },
            {
                header: t('invoice-number-office'),
                id: 'invoiceNumber',
                accessorKey: 'invoiceNumber',
                enableSorting: !!isSortingEnabled,
                meta: {
                    widthWeight: 1.2,
                    minPx: 130,
                },
                cell: ({ row }) => (
                    <div className="flex flex-col gap-1">
                        <InvoiceNumber invoice={row.original} contactId={contactId} matterId={matterId} />
                        {row.original.invoicedByOffice ? (
                            <RecordLink
                                className="entity-link text-fg hover:text-link w-fit no-underline hover:underline"
                                to={`/office/${row.original.invoicedByOffice.id}`}
                                label={`${row.original.invoicedByOffice.abbreviation}${
                                    officeInactiveSuffix(row.original.invoicedByOffice) ?? ''
                                }`}
                                withOrigin
                            />
                        ) : null}
                    </div>
                ),
            },
            {
                header: t('invoices.client-payer'),
                id: 'clientPayer',
                accessorKey: 'clientParty',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'clientParty.name',
                    widthWeight: 1.5,
                    minPx: 160,
                },
                cell: ({ row }) => <InvoiceClientPayer invoice={row.original} />,
            },
            {
                header: t('invoices.primary-contact'),
                accessorKey: 'primaryRecipientParty',
                enableSorting: false,
                meta: {
                    widthWeight: 1.5,
                    minPx: 160,
                },
                cell: ({ getValue }) => {
                    const value = getValue();
                    return value ? <PartyName party={value} isLink useLinkWithOrigin variant="list" /> : null;
                },
            },
            buildCaseColumn(),
            {
                header: t('invoices.gross-total'),
                id: 'grossTotal',
                accessorKey: 'invoiceTotals',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'totals.baseCurrency.invoice.totalWithVat',
                    widthWeight: 1,
                    minPx: 140,
                    renderTotal: renderMoneyTotal('totalGrossTotalBaseCurrency'),
                },
                cell: ({ row }) => <InvoiceBrutoTotal invoice={row.original} />,
            },
            {
                header: t('invoices.prepayment'),
                id: 'prepayment',
                accessorKey: 'invoiceTotals',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'paymentsJson.baseCurrency.prepaymentAmount',
                    widthWeight: 1,
                    minPx: 140,
                    renderTotal: renderMoneyTotal('totalPrepaymentBaseCurrency'),
                },
                cell: ({ row }) => <InvoicePrepaymentAmount invoice={row.original} />,
            },
            {
                header: () => (
                    <TooltipHeader
                        label="invoices.final-payable-amount"
                        tooltip="invoices.tooltips.final-payable-amount"
                    />
                ),
                id: 'finalPayableAmount',
                accessorKey: 'finalPayableAmountInPaymentCurrency',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'paymentsJson.baseCurrency.amountDue',
                    widthWeight: 1,
                    minPx: 140,
                    pickerLabel: t('invoices.final-payable-amount'),
                    renderTotal: renderMoneyTotal('totalFinalPayableBaseCurrency'),
                },
                cell: ({ row }) => <InvoiceAmountDisplay invoice={row.original} fieldName="amountDue" />,
            },
            {
                header: t('invoices.outstanding-balance'),
                id: 'invoicesOutstandingBalance',
                accessorKey: 'outstandingBalanceInPaymentCurrency',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'paymentsJson.baseCurrency.unpaidAmount',
                    widthWeight: 1,
                    minPx: 140,
                    renderTotal: renderMoneyTotal('totalOutstandingBalanceBaseCurrency'),
                },
                cell: ({ row }) => <InvoiceOutstandingBalance invoice={row.original} />,
            },
            {
                header: t('invoices.next-reminder-date'),
                accessorKey: 'nextReminderDate',
                enableSorting: false,
                meta: {
                    widthWeight: 0.8,
                    minPx: DATE_COLUMN_MIN_PX,
                },
                cell: ({ getValue }) => {
                    const value = getValue();
                    if (!value) return null;
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const reminderDate = new Date(value);
                    reminderDate.setHours(0, 0, 0, 0);
                    const isPastDate = reminderDate < today;
                    return <DateDefaultNowrap dateValue={value} className={isPastDate ? 'text-error' : undefined} />;
                },
            },
        ],
        [isSortingEnabled, classifiers, contactId, matterId]
    );

    const accountingView = useMemo<InvoiceColumn[]>(
        () => [
            {
                header: t('date'),
                id: 'invoiceDate',
                accessorKey: 'invoiceDate',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sticky: true,
                    lockVisible: true,
                    widthWeight: 0.9,
                    minPx: DATE_WITH_TYPE_COLUMN_MIN_PX,
                },
                cell: ({ row }) => <InvoiceDate invoice={row.original} classifiers={classifiers} />,
            },
            {
                header: t('invoices.due-date'),
                id: 'dueDate',
                accessorKey: 'dueDate',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'dueDate',
                    nullHandling: 'nullsLast',
                    widthWeight: 0.8,
                    minPx: DATE_COLUMN_MIN_PX,
                },
                cell: ({ getValue }) => {
                    const value = getValue();
                    return value && <DateDefaultNowrap dateValue={value} />;
                },
            },
            {
                header: t('invoices.status-change'),
                id: 'statusChange',
                accessorKey: 'statusChangeDate',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'sortParamsJson.lastStatusChangeDate',
                    nullHandling: 'nullsLast',
                    widthWeight: 1.2,
                    // A nowrap date over a nowrap status label; no type chip in this cell.
                    minPx: 160,
                },
                cell: ({ row }) => (
                    <InvoiceStatusChange
                        invoice={row.original}
                        classifiers={classifiers}
                        contactId={contactId}
                        matterId={matterId}
                    />
                ),
            },
            {
                header: t('invoices.invoice-number'),
                id: 'invoiceNumber',
                accessorKey: 'invoiceNumber',
                enableSorting: !!isSortingEnabled,
                meta: {
                    widthWeight: 1,
                    minPx: 130,
                },
                cell: ({ row }) => <InvoiceNumber invoice={row.original} contactId={contactId} matterId={matterId} />,
            },
            {
                header: t('invoices.credits-no'),
                id: 'creditInvoices',
                enableSorting: false,
                meta: {
                    widthWeight: 0.8,
                    minPx: 90,
                },
                cell: ({ row }) => <InvoiceCreditInvoices invoice={row.original} />,
            },
            {
                header: t('matters.client'),
                id: 'clients',
                enableSorting: false,
                meta: {
                    widthWeight: 1.5,
                    minPx: 160,
                },
                cell: ({ row }) => <InvoiceClient invoice={row.original} />,
            },
            {
                header: t('matters.payer'),
                accessorKey: 'clientParty',
                enableSorting: false,
                meta: {
                    widthWeight: 1.5,
                    minPx: 160,
                },
                cell: ({ getValue }) => {
                    const value = getValue();
                    return value ? <PartyName party={value} isLink useLinkWithOrigin variant="list" /> : null;
                },
            },
            {
                header: t('invoices.currency'),
                accessorKey: 'paymentCurrencyTypeCode',
                enableSorting: false,
                meta: {
                    widthWeight: 0.5,
                    minPx: 70,
                },
                cell: ({ getValue }) => getValue() ?? '',
            },
            {
                header: t('invoices.paid-on'),
                id: 'paidOnDate',
                accessorKey: 'paidOnDate',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'paidOnDate',
                    nullHandling: 'nullsLast',
                    widthWeight: 0.8,
                    minPx: DATE_COLUMN_MIN_PX,
                },
                cell: ({ getValue }) => {
                    const value = getValue();
                    return value && <DateDefaultNowrap dateValue={value} />;
                },
            },
            {
                header: t('invoices.fees-with-discount'),
                id: 'feesWithDiscount',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 140,
                    renderTotal: renderMoneyTotal('totalFeesBaseCurrency'),
                },
                cell: ({ row }) => <FeesWithDiscount invoice={row.original} />,
            },
            {
                header: t('invoices.vat-total'),
                id: 'vatTotal',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 140,
                    renderTotal: renderMoneyTotal('totalVatBaseCurrency'),
                },
                cell: ({ row }) => <InvoiceVatTotal invoice={row.original} />,
            },
            {
                header: t('invoices.gross-total'),
                id: 'grossTotal',
                accessorKey: 'invoiceTotals',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'totals.baseCurrency.invoice.totalWithVat',
                    widthWeight: 1,
                    minPx: 140,
                    renderTotal: renderMoneyTotal('totalGrossTotalBaseCurrency'),
                },
                cell: ({ row }) => <InvoiceBrutoTotal invoice={row.original} />,
            },
            {
                header: t('invoices.prepayment'),
                id: 'prepayment',
                accessorKey: 'invoiceTotals',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'paymentsJson.baseCurrency.prepaymentAmount',
                    widthWeight: 1,
                    minPx: 140,
                    renderTotal: renderMoneyTotal('totalPrepaymentBaseCurrency'),
                },
                cell: ({ row }) => <InvoicePrepaymentAmount invoice={row.original} />,
            },
            {
                header: () => (
                    <TooltipHeader
                        label="invoices.final-payable-amount"
                        tooltip="invoices.tooltips.final-payable-amount"
                    />
                ),
                id: 'finalPayableAmount',
                accessorKey: 'finalPayableAmountInPaymentCurrency',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'paymentsJson.baseCurrency.amountDue',
                    widthWeight: 1,
                    minPx: 140,
                    pickerLabel: t('invoices.final-payable-amount'),
                    renderTotal: renderMoneyTotal('totalFinalPayableBaseCurrency'),
                },
                cell: ({ row }) => <InvoiceAmountDisplay invoice={row.original} fieldName="amountDue" />,
            },
            {
                header: () => <TooltipHeader label="invoices.sent-pdf" tooltip="invoices.sent-pdf-tooltip" />,
                id: 'sentPdf',
                enableSorting: false,
                meta: {
                    widthWeight: 0.8,
                    minPx: 100,
                    pickerLabel: t('invoices.sent-pdf'),
                },
                cell: ({ row }) => <SentPdfCell invoice={row.original} />,
            },
        ],
        [isSortingEnabled, classifiers, contactId, matterId]
    );

    const ksefView = useMemo<InvoiceColumn[]>(
        () => [
            {
                header: t('invoices.invoice-no-client-payer'),
                id: 'invoiceNumber',
                enableGrouping: false,
                accessorKey: 'invoiceNumber',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sticky: true,
                    lockVisible: true,
                    widthWeight: 2.5,
                    minPx: 240,
                },
                cell: ({ row }) => (
                    <InvoiceNumberClientPayer
                        invoice={row.original}
                        classifiers={classifiers}
                        contactId={contactId}
                        matterId={matterId}
                    />
                ),
            },
            buildCaseColumn(),
            {
                header: t('date'),
                id: 'invoiceDate',
                accessorKey: 'invoiceDate',
                enableSorting: !!isSortingEnabled,
                meta: {
                    widthWeight: 0.8,
                    minPx: DATE_COLUMN_MIN_PX,
                },
                cell: ({ getValue }) => {
                    const value = getValue();
                    return value && <DateDefaultNowrap dateValue={value} />;
                },
            },
            {
                header: t('invoices.status-change'),
                id: 'statusChange',
                accessorKey: 'statusChangeDate',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'sortParamsJson.lastStatusChangeDate',
                    nullHandling: 'nullsLast',
                    widthWeight: 1.2,
                    // A nowrap date over a nowrap status label; no type chip in this cell.
                    minPx: 160,
                },
                cell: ({ row }) => (
                    <InvoiceStatusChange
                        invoice={row.original}
                        classifiers={classifiers}
                        contactId={contactId}
                        matterId={matterId}
                    />
                ),
            },
            {
                header: t('invoices.currency'),
                accessorKey: 'paymentCurrencyTypeCode',
                enableSorting: false,
                meta: {
                    widthWeight: 0.5,
                    minPx: 70,
                },
                cell: ({ getValue }) => getValue() ?? '',
            },
            {
                header: t('invoices.ksef.column.ksef-status'),
                id: 'externalStatusTypeCode',
                accessorKey: 'externalStatusTypeCode',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 130,
                },
                cell: ({ getValue }) => {
                    const value = getValue();
                    return value && KSEF_STATUS_TRANSLATION_KEYS[value]
                        ? t(KSEF_STATUS_TRANSLATION_KEYS[value])
                        : (value ?? '-');
                },
            },
            {
                header: t('invoices.ksef.column.external-ksef-status'),
                id: 'externalActionTypeCode',
                accessorKey: 'externalActionTypeCode',
                enableSorting: false,
                meta: {
                    widthWeight: 1.2,
                    minPx: 130,
                },
                cell: ({ getValue }) => {
                    const value = getValue();
                    return value && KSEF_EXTERNAL_ACTION_TRANSLATION_KEYS[value]
                        ? t(KSEF_EXTERNAL_ACTION_TRANSLATION_KEYS[value])
                        : (value ?? '-');
                },
            },
            {
                header: t('invoices.ksef.column.external-invoice-number'),
                id: 'externalInvoiceNumber',
                accessorKey: 'externalInvoiceNumber',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'externalEvents.externalInvoiceNumber',
                    nullHandling: 'nullsLast',
                    widthWeight: 1.2,
                    minPx: 130,
                },
                cell: ({ getValue }) => getValue() ?? '-',
            },
            {
                header: t('invoices.ksef.column.external-id'),
                id: 'externalEventId',
                accessorKey: 'externalEventId',
                enableSorting: !!isSortingEnabled,
                meta: {
                    sortAccessor: 'externalEvents.externalId',
                    nullHandling: 'nullsLast',
                    widthWeight: 1,
                    minPx: 130,
                },
                cell: ({ getValue }) => getValue() ?? '-',
            },
            {
                header: t('invoices.ksef.column.external-status'),
                id: 'externalInvoiceState',
                accessorKey: 'externalInvoiceState',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 130,
                },
                cell: ({ getValue }) => {
                    const value = getValue();
                    if (!value) return '-';
                    const key = KSEF_EXTERNAL_STATE_TRANSLATION_KEYS[value];
                    return key ? t(key) : value;
                },
            },
            {
                header: t('invoices.ksef.column.external-status-reason'),
                id: 'externalInvoiceStateReason',
                accessorKey: 'externalInvoiceStateReason',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 130,
                },
                cell: ({ getValue }) => {
                    const value = getValue();
                    if (!value) return '-';
                    const truncated = value.length > 30 ? `${value.slice(0, 30)}…` : value;
                    return (
                        <Tooltip content={value}>
                            <span>{truncated}</span>
                        </Tooltip>
                    );
                },
            },
        ],
        [isSortingEnabled, classifiers, contactId, matterId]
    );

    const filterConfig = useInvoiceFilterConfig({
        classifiers,
        passiveFilter,
        invoiceTemplateList,
        disabledGroups,
        disabledFilterIds,
        paginatedFieldPassiveFilters,
        isKsefTabEnabled,
    });

    const tabGallery = useMemo(
        () => [globalView, lawyersView, revenueTeamView, creditManagementView, accountingView, ksefView],
        [globalView, lawyersView, revenueTeamView, creditManagementView, accountingView, ksefView]
    );

    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const tabFromUrl = searchParams.get('invoiceTab');
    const initialTabFromUrl = tabFromUrl ? TAB_URL_PARAM_MAP[tabFromUrl] : undefined;

    const [storedTab, setStoredTab] = useLocalStorage<number>('invoice-selected-tab', 0);
    const [currentTab, setCurrentTab] = useState<number>(initialTabFromUrl ?? storedTab);

    const isPathBased = pathBasedTabId !== undefined;
    const tabFromPath = pathBasedTabId !== undefined ? TAB_URL_PARAM_MAP[pathBasedTabId] : undefined;
    // In the new design the tabs are the list's built-in views, in its views row; without a path of their own,
    // the URL's view names the tab.
    const tabsAsViews = useIsNewDesign() && !disableUrlState;
    const viewTabKey = useRecordTabBuiltIn(
        tabsAsViews && !isPathBased ? 'invoices' : undefined,
        TAB_URL_KEYS,
        TAB_INDEX_TO_URL_KEY[currentTab as InvoiceTableTab] ?? DEFAULT_INVOICE_TAB_URL_KEY
    );
    const resolvedTab = isPathBased
        ? (tabFromPath ?? InvoiceTableTab.MAIN)
        : tabsAsViews
          ? (TAB_URL_PARAM_MAP[viewTabKey ?? ''] ?? InvoiceTableTab.MAIN)
          : currentTab;
    const activeTab = resolvedTab === InvoiceTableTab.KSEF && !isKsefTabEnabled ? InvoiceTableTab.MAIN : resolvedTab;

    const mergedColumns = useMemo(() => buildTabColumns(tabGallery, activeTab), [tabGallery, activeTab]);

    const selectedDebtReminderInvoiceIds = useSelector(
        (state: RootState) => state.invoices.selectedDebtReminderInvoiceIds
    );

    const buildPathForTabKey = (tabKey: string): string => {
        const segments = location.pathname.split('/').filter(Boolean);
        const lastSegment = segments[segments.length - 1];
        const hasTrailingTab = lastSegment !== undefined && lastSegment in TAB_URL_PARAM_MAP;
        const baseSegments = hasTrailingTab ? segments.slice(0, -1) : segments;
        return '/' + [...baseSegments, tabKey].join('/');
    };

    const handleTabChange = (value: number) => {
        setStoredTab(value); // Save to localStorage for cross-mode default + legacy compat
        if (isPathBased) {
            const tabKey = TAB_INDEX_TO_URL_KEY[value as InvoiceTableTab];
            if (tabKey) {
                navigate(buildPathForTabKey(tabKey));
            }
            return;
        }
        setCurrentTab(value);
    };

    // Clear bulk send selections when switching away from Revenue Team tab
    useEffect(() => {
        if (activeTab !== InvoiceTableTab.REVENUE_TEAM) {
            dispatch(clearBulkSendInvoices());
        }
    }, [activeTab, dispatch]);

    const tabViews: { value: InvoiceTableTab; labelId: string; tooltipId: string }[] = [
        {
            value: InvoiceTableTab.MAIN,
            labelId: 'invoices.table-view.global',
            tooltipId: 'invoices.table-view.global-tooltip',
        },
        {
            value: InvoiceTableTab.LAWYERS,
            labelId: 'invoices.table-view.law',
            tooltipId: 'invoices.table-view.law-tooltip',
        },
        {
            value: InvoiceTableTab.REVENUE_TEAM,
            labelId: 'invoices.table-view.rev',
            tooltipId: 'invoices.table-view.rev-tooltip',
        },
        {
            value: InvoiceTableTab.CREDIT_MANAGEMENT,
            labelId: 'invoices.table-view.cr',
            tooltipId: 'invoices.table-view.cr-tooltip',
        },
        {
            value: InvoiceTableTab.ACCOUNTING,
            labelId: 'invoices.table-view.acct',
            tooltipId: 'invoices.table-view.acct-tooltip',
        },
        ...(isKsefTabEnabled
            ? [
                  {
                      value: InvoiceTableTab.KSEF,
                      labelId: 'invoices.table-view.ksef',
                      tooltipId: 'invoices.table-view.ksef-tooltip',
                  },
              ]
            : []),
    ];

    const getInvoiceHref = useCallback(
        (invoice: InvoiceDataListItem) => invoicePath(invoice.id, { contactId, matterId }),
        [contactId, matterId]
    );

    // As views, the tabs are rendered by the list, in its views row, where each name is written in full: only KSeF's
    // acronym needs its tooltip.
    const viewsPage = {
        listKey: 'invoices' as const,
        views: tabViews.map(({ value, labelId, tooltipId }) => ({
            id: TAB_INDEX_TO_URL_KEY[value],
            labelId,
            tooltipId: value === InvoiceTableTab.KSEF ? tooltipId : undefined,
        })),
        activeId: TAB_INDEX_TO_URL_KEY[activeTab as InvoiceTableTab],
        pathFor: isPathBased ? buildPathForTabKey : undefined,
        onSelect: (viewId: string) => {
            setStoredTab(TAB_URL_PARAM_MAP[viewId]);
            navigate({
                pathname: isPathBased ? buildPathForTabKey(viewId) : location.pathname,
                search: withSavedView('', viewId),
            });
        },
    };

    const renderCustomHeader = () => (
        <LineTabs value={activeTab} onValueChange={(value) => handleTabChange(Number(value))}>
            <LineTabsList>
                {tabViews.map(({ value, labelId, tooltipId }) => (
                    <Tooltip key={value} content={<FormattedMessage id={tooltipId} />}>
                        <LineTab value={value}>
                            <FormattedMessage id={labelId} />
                        </LineTab>
                    </Tooltip>
                ))}
            </LineTabsList>
        </LineTabs>
    );

    if (isLoadingClassifiers || isLoadingInvoiceTemplates || viewTabKey === undefined) {
        return <Loader />;
    }

    const selectedBulkSendInvoices = (data?.content || []).filter((inv) => selectedBulkSendInvoiceIds.includes(inv.id));

    const isSelectedItemsWithSameClientParty = selectedBulkSendInvoices.every(
        (item, _, array) => array.length > 0 && item.clientParty.id === array[0].clientParty.id
    );

    return (
        <ListViewsPageContext.Provider value={tabsAsViews ? viewsPage : undefined}>
            <ListTableWithFilters<InvoiceDataListItem>
                getRowHref={getInvoiceHref}
                // All six tabs go through the same chrome, each with its own column array, and
                // `columnConfigId` keeps persisted widths/order carrying over per tab. Invoices keeps
                // the full managed-table toolkit; these affordances are opt-in
                // per table (default off) so a managed table like Roles & Permissions doesn't inherit them.
                enableColumnPicker
                enableColumnReorder
                enableColumnResize
                scrollRestorationId="invoices-list"
                renderCustomHeader={tabsAsViews ? undefined : renderCustomHeader}
                columns={mergedColumns}
                columnConfigId={`invoices-${TAB_INDEX_TO_URL_KEY[activeTab as InvoiceTableTab]}`}
                // Hide the previous filters' rows while a new combination loads, so the
                // row skeleton replaces the list instead of trailing below stale rows.
                data={tableLoading.isLoading ? undefined : data}
                isLoading={isLoadingClassifiers || tableLoading.isLoading}
                isFetching={tableLoading.isFetching}
                pluralTextId="invoices.invoices"
                singularTextId="invoices.invoice"
                noEntriesId="invoices.no-invoices-found"
                noEntriesBodyId="invoices.empty.body"
                noEntriesIcon={ReceiptLongOutlinedIcon}
                noEntriesAction={
                    <PermissionButton
                        permissions={['CREATE.INVOICES.MATTER_ACCESS']}
                        variant="contained"
                        color="secondary"
                        onClick={() => navigateWithReturn('/billable-clients')}
                    >
                        <FormattedMessage id="billable-clients.go-to-billable-clients" />
                    </PermissionButton>
                }
                fetchData={fetchData}
                fetchCount={useLazyGetInvoicesCountQuery}
                fetchTotals={totalsEnabled ? useGetInvoicesTotalsQuery : undefined}
                onDemandTotals={totalsEnabled}
                defaultTotalsRowRequested={defaultShowTotals}
                totalsLimit={TOTALS_RECORD_LIMIT}
                totalsLimitExceededMessageId="invoices.totals.limit-exceeded"
                filterConfig={filterConfig}
                rowsPerPage={50}
                disableUrlState={disableUrlState}
                extraHeaderComponent={extraHeaderComponent}
                exportHookResults={
                    disableExport || !hasPermissionToExportInvoices
                        ? undefined
                        : [
                              {
                                  labelKey: 'export',
                                  isBusy: isExportDataFetching,
                                  triggerManualDownload,
                              },
                              ...(isCRExportEnabled
                                  ? [
                                        {
                                            labelKey: 'invoices.export-cr',
                                            isBusy: isCRExportDataFetching,
                                            triggerManualDownload: triggerCRDownload,
                                        },
                                    ]
                                  : []),
                          ]
                }
                renderSearchDescriptionLevelComponent={() => {
                    const showsCreditActions = activeTab === InvoiceTableTab.CREDIT_MANAGEMENT;
                    const showsBulkSend = activeTab === InvoiceTableTab.REVENUE_TEAM && isBulkSendEnabled;
                    // An empty wrapper would still take its margin and a gap in the quick-filter controls.
                    if (!showsCreditActions && !showsBulkSend) {
                        return null;
                    }

                    // Filter out only the selected invoices
                    const selectedInvoices = (data?.content || []).filter((inv) =>
                        selectedDebtReminderInvoiceIds.includes(inv.id)
                    );

                    const debtReminderEligibleInvoices = (data?.content || []).filter((inv) =>
                        isDebtReminderEligible(inv)
                    );

                    return (
                        <div className="ml-4 flex flex-row gap-2">
                            {showsCreditActions && (
                                <React.Fragment>
                                    <BulkSelectButton invoices={debtReminderEligibleInvoices} />
                                    <AddDebtNoteButton invoices={selectedInvoices} />
                                    <DebtReminderButton />
                                </React.Fragment>
                            )}
                            {showsBulkSend && (
                                <SendInvoicesButton
                                    variant="outlined"
                                    className="bg-paper!"
                                    size="small"
                                    invoices={selectedBulkSendInvoices}
                                    disabled={
                                        !isSelectedItemsWithSameClientParty || selectedBulkSendInvoices.length === 0
                                    }
                                    emailTemplateType={InvoiceEmailTemplateType.BULK_INVOICES}
                                    getIsSpecIncluded={() => false}
                                    transitionToSent={true}
                                >
                                    {t('invoices.bulk-send')}
                                </SendInvoicesButton>
                            )}
                        </div>
                    );
                }}
            />
        </ListViewsPageContext.Provider>
    );
};

export default InvoicesTable;
